<template>
  <modal name="safari-cancellation-detail" width="96%" :max-width="kind === 'group' ? 1440 : 1080" height="auto" :adaptive="true" :scrollable="true" :click-to-close="!busy" @before-close="beforeClose" @opened="focusClose">
    <section class="safari-cancellation-modal" role="dialog" aria-modal="true" aria-labelledby="safari-cancellation-title">
      <AppModalHeader ref="header" design="new" :title="title" :eyebrow="kind === 'group' ? 'Cancelled Safari group' : finance ? 'Client refund' : 'Snackbar refund'" title-id="safari-cancellation-title" @close="close" />
      <div v-if="loading" class="cancellation-content" aria-busy="true"><div v-for="n in 3" :key="n" class="cancellation-skeleton" /></div>
      <div v-else-if="error" class="cancellation-content"><p role="alert">{{ error }}</p><AppButton design="new" @click.native="load">Retry</AppButton></div>
      <div v-else-if="detail && kind === 'group'" class="cancellation-content">
        <div class="cancellation-summary-grid">
          <SectionCard title="Cancellation">
            <template #actions><AppStatusBadge design="new" compact tone="red" label="Cancelled" /></template>
            <dl class="cancellation-facts">
              <div><dt>Cancelled on</dt><dd>{{ dateLabel(detail.cancelled_at) }}</dd></div>
              <div><dt>Cancelled by</dt><dd>{{ detail.cancelled_by_name || 'System' }}</dd></div>
              <div><dt>Manager</dt><dd>{{ detail.manager_name || 'Unassigned' }}</dd></div>
            </dl>
          </SectionCard>
          <SectionCard title="Safari group">
            <dl class="cancellation-facts">
              <div><dt>Original group</dt><dd>#{{ detail.original_group_id }}</dd></div>
              <div><dt>Reservation</dt><dd><router-link v-if="detail.reservation_id" :to="`/reservation/safari/${detail.reservation_id}`">#{{ detail.reservation_id }}</router-link><span v-else>—</span></dd></div>
              <div><dt>Participants</dt><dd>{{ detail.participants.length }}</dd></div>
            </dl>
          </SectionCard>
          <SectionCard title="Safari &amp; route">
            <dl class="cancellation-facts">
              <div><dt>Tour dates</dt><dd>{{ dateLabel(detail.tour_start) }} — {{ dateLabel(detail.tour_end) }}</dd></div>
              <div><dt>Type</dt><dd>{{ detail.snapshot.group_type.name }}</dd></div>
              <div><dt>Start</dt><dd>{{ detail.snapshot.locations.start_from || '—' }}</dd></div>
              <div><dt>Finish</dt><dd>{{ detail.snapshot.locations.finish_at || '—' }}</dd></div>
            </dl>
          </SectionCard>
          <SectionCard title="Reason &amp; notes">
            <p class="cancellation-note">{{ detail.reason || 'Group removed through the existing cancellation workflow.' }}</p>
            <details v-if="groupData.short_itinerary || groupData.notes">
              <summary>Itinerary &amp; group notes</summary>
              <p v-if="groupData.short_itinerary" class="cancellation-note">{{ groupData.short_itinerary }}</p>
              <p v-if="groupData.notes" class="cancellation-note">{{ groupData.notes }}</p>
            </details>
          </SectionCard>
          <SectionCard title="Snackbar preparation">
            <dl class="cancellation-facts">
              <div v-for="step in snackSteps.slice(0, 3)" :key="step.key"><dt>{{ step.label }}</dt><dd>{{ snackData[step.key] ? 'Yes' : 'No' }}</dd></div>
            </dl>
          </SectionCard>
          <SectionCard title="Snackbar handover">
            <dl class="cancellation-facts">
              <div v-for="step in snackSteps.slice(3)" :key="step.key"><dt>{{ step.label }}</dt><dd>{{ snackData[step.key] ? 'Yes' : 'No' }}</dd></div>
            </dl>
            <p v-if="snackData.notes" class="cancellation-note">{{ snackData.notes }}</p>
          </SectionCard>
        </div>
        <SectionCard :title="`Participants & Snackbar (${detail.participants.length})`">
          <AppDataTable design="new" :columns="participantColumns" :rows="detail.participants" min-width="1000px">
            <template #cell-flying_doctor="{ row }">{{ row.flying_doctor || 'Not recorded' }}<small class="cancellation-meta">{{ row.flying_doctor_price === null ? 'No price recorded' : money(row.flying_doctor_price) }}</small></template>
            <template #cell-paid="{ row }">{{ money(row.refund && row.refund.paid_amount !== undefined ? row.refund.paid_amount : row.snapshot.participant.snack_bar_credit) }}</template>
            <template #cell-order="{ row }"><AppButton v-if="orders(row).length" design="new" theme="dark" size="small" @click.native="showOrder(row)">View order · {{ orders(row).length }}</AppButton><span v-else class="cancellation-muted">No items</span></template>
            <template #cell-refund="{ row }"><AppButton v-if="row.refund && row.refund.id" design="new" theme="dark" @click.native="$emit('select', { kind: 'refund', id: row.refund.id })">{{ refundState(row.refund.status).name }}</AppButton><AppStatusBadge v-else-if="row.refund" design="new" :label="refundState(row.refund.status).name" :tone="refundState(row.refund.status).tone" /><span v-else>No paid Snackbar recorded</span></template>
            <template #empty><p>No participants recorded.</p></template>
          </AppDataTable>
        </SectionCard>
        <SectionCard v-if="orderPerson" :title="`Snackbar order · ${orderPerson.name}`">
          <template #actions><AppButton design="new" theme="dark" size="small" @click.native="hideOrder">Close order</AppButton></template>
          <div ref="orderDetail" tabindex="-1" class="cancellation-order-detail">
            <AppDataTable design="new" :columns="orderColumns" :rows="orders(orderPerson)" min-width="550px">
              <template #cell-product="{ row }">{{ (row.product && row.product.name) || (row.item_snapshot && row.item_snapshot.name) || 'Item' }}<small v-if="row.product" class="cancellation-meta">{{ row.product.portion_size }}</small></template>
              <template #cell-price="{ row }">{{ money(row.price) }}</template>
              <template #cell-total="{ row }">{{ money(row.price * row.count) }}</template>
            </AppDataTable>
          </div>
        </SectionCard>
        <AppCard v-if="detail.can_view_archive" design="new" class="cancellation-archive">
          <details @toggle="archiveOpen = $event.target.open">
            <summary>Cancellation archive</summary>
            <div v-if="archiveOpen" class="cancellation-archive__content">
              <p class="cancellation-hint">Saved at cancellation. Dates and times are shown in Tanzania time.</p>
              <AppFormField design="new" label="Archive section" label-for="cancellation-archive-section" class="cancellation-archive__picker"><AppSelect v-model="archiveKey" design="new" input-id="cancellation-archive-section" :options="archiveSections" :reduce="option => option.key" label="label" :clearable="false" /></AppFormField>
              <SnapshotTable v-if="archiveSection" :key="archiveSection.key" :value="archiveSection.value" />
              <p v-else class="cancellation-hint">No saved records.</p>
            </div>
          </details>
        </AppCard>
      </div>
      <RefundPanel v-else-if="detail" :key="detail.id" :refund="detail" @busy="busy = $event" @updated="updated" @group="$emit('select', { kind: 'group', id: $event })" />
    </section>
  </modal>
</template>
<script>
import AppCard from '@/components/AppCard'
import AppSelect from '@/components/AppSelect'
import AppFormField from '@/components/AppFormField'
import AppModalHeader from '@/components/AppModalHeader'
import SectionCard from './SectionCard'
import AppButton from '@/components/AppButton'
import AppStatusBadge from '@/components/AppStatusBadge'
import AppDataTable from '@/components/AppDataTable'
import SnapshotTable from './SnapshotTable'
import RefundPanel from './RefundPanel'
import { getCancelledSafariGroup, getSafariSnackbarRefund, getFinanceRefund } from '@/http'
import { normalizeFinanceRefund } from '@/utils/safariRefundAccess'
import { dateLabel, money, refundState, errorMessage } from './format'
export default {
  name: 'SafariCancellationDetailModal',
  components: { AppCard, AppSelect, AppFormField, AppModalHeader, SectionCard, AppButton, AppStatusBadge, AppDataTable, SnapshotTable, RefundPanel },
  props: { selection: { type: Object, default: null }, finance: { type: Boolean, default: false } },
  data() {
    return {
      detail: null,
      loading: false,
      error: '',
      busy: false,
      requestId: 0,
      orderPerson: null,
      orderOpener: null,
      archiveOpen: false,
      archiveKey: 'safari.group',
      participantColumns: [{ key: 'name', label: 'Client' }, { key: 'flying_doctor', label: 'Flying Doctor' }, { key: 'paid', label: 'Snackbar paid' }, { key: 'order', label: 'Snackbar order' }, { key: 'refund', label: 'Refund' }],
      orderColumns: [{ key: 'product', label: 'Item' }, { key: 'count', label: 'Quantity' }, { key: 'price', label: 'Unit price' }, { key: 'total', label: 'Total' }],
      snackSteps: [{ key: 'orders_available', label: 'Orders available' }, { key: 'collected', label: 'Collected' }, { key: 'summary_printed', label: 'Summary printed' }, { key: 'packing_started', label: 'Packing started' }, { key: 'passed_to_driver', label: 'Passed to driver' }]
    }
  },
  computed: {
    archiveSections() {
      if (!this.detail || this.kind !== 'group') return []
      const snapshot = this.detail.snapshot
      return [
        ...Object.entries(snapshot.deleted_rows || {}).map(([key, value]) => ({ key, label: this.modelLabel(key), value })),
        { key: 'group_type', label: 'Group type', value: snapshot.group_type },
        { key: 'staff', label: 'Staff at cancellation', value: snapshot.staff },
        { key: 'reservation', label: 'Reservation', value: snapshot.reservation },
        { key: 'transfers', label: 'Linked transfers', value: snapshot.transfers },
        { key: 'client_details', label: 'Client & payment details', value: this.detail.participants.map(person => ({ client_name: person.name, ...person.snapshot })) }
      ].filter(section => section.value && Object.keys(section.value).length)
    },
    archiveSection() { return this.archiveSections.find(section => section.key === this.archiveKey) || this.archiveSections[0] },
    kind() { return this.selection && this.selection.kind },
    title() { return this.detail ? this.kind === 'group' ? this.detail.name : this.detail.client_name : 'Cancellation details' },
    groupData() { return this.detail.snapshot.deleted_rows['safari.group'][0] || {} },
    snackData() { return (this.detail.snapshot.deleted_rows['safari.snackbarinfo'] || [])[0] || {} }
  },
  watch: { selection: { deep: true, handler() { this.sync() } } },
  mounted() { this.sync() },
  beforeDestroy() { this.requestId += 1 },
  methods: {
    orders(person) { return (person.snapshot && person.snapshot.orders) || [] },
    showOrder(person) { this.orderOpener = document.activeElement; this.orderPerson = person; this.$nextTick(() => { this.$refs.orderDetail.focus(); this.$refs.orderDetail.scrollIntoView({ block: 'nearest' }) }) },
    hideOrder() { this.orderPerson = null; this.$nextTick(() => { if (this.orderOpener && this.orderOpener.isConnected) this.orderOpener.focus() }) },
    money,
    dateLabel,
    refundState,
    modelLabel(model) { return { 'safari.group': 'Group', 'safari.groupclient': 'Participants', 'safari.order': 'Snackbar orders', 'safari.snackbarinfo': 'Snackbar preparation', 'transfers.transfersafarigroup': 'Transfer links' }[model] || model },
    sync() {
      if (this.selection) { this.$modal.show('safari-cancellation-detail'); this.load() } else { this.$modal.hide('safari-cancellation-detail') }
    },
    async load() {
      if (!this.selection) return
      const current = ++this.requestId
      this.busy = false
      this.loading = true
      this.error = ''
      this.detail = null
      this.orderPerson = null
      this.archiveOpen = false
      this.archiveKey = 'safari.group'
      try {
        const fn = this.kind === 'group' ? getCancelledSafariGroup : this.finance ? getFinanceRefund : getSafariSnackbarRefund
        const { data } = await fn(this.selection.id)
        if (current === this.requestId) this.detail = this.finance && this.kind === 'refund' ? normalizeFinanceRefund(data) : data
      } catch (error) { if (current === this.requestId) this.error = errorMessage(error) } finally { if (current === this.requestId) { this.loading = false; this.$nextTick(this.focusClose) } }
    },
    focusClose() { if (this.$refs.header) this.$refs.header.focusClose() },
    close() { if (!this.busy) this.$modal.hide('safari-cancellation-detail') },
    beforeClose(event) { if (this.busy) { event.cancel(); return } this.requestId += 1; this.$emit('close') },
    updated(data) { this.detail = data; this.$emit('updated') }
  }
}
</script>

<style scoped lang="scss">
.cancellation-archive__content { padding-top: $ui-space-8; }
.cancellation-archive__picker { max-width: 620px; margin-bottom: $ui-space-20; }
.cancellation-muted { color: $ui-muted; }
.cancellation-order-detail:focus { outline: none; }
</style>
